import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Check } from 'lucide-react';
import { MessageModal } from '../../components/modals/MessageModal';
export function SignupPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: 'buyer',
    agreeToTerms: false
  });
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreeToTerms) {
      setModalContent({
        title: 'Terms Required',
        message: 'Please agree to the Terms of Use and Privacy Policy to continue.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setModalContent({
        title: 'Password Mismatch',
        message: 'Passwords do not match. Please try again.',
        type: 'error'
      });
      setIsMessageModalOpen(true);
      return;
    }
    // Simulate signup success
    setModalContent({
      title: 'Account Created',
      message: 'Your account has been created successfully. Please check your email for OTP verification.',
      type: 'success'
    });
    setIsMessageModalOpen(true);
    // Redirect to OTP verification after modal closes
    setTimeout(() => {
      navigate('/auth/verify-otp', {
        state: {
          email: formData.email
        }
      });
    }, 2000);
  };
  const handleSocialLogin = (provider: string) => {
    setModalContent({
      title: `${provider} Login`,
      message: `Redirecting to ${provider} authentication... In production, this would use OAuth 2.0 flow.`,
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  return <div className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933]" data-id="element-2278">
      {/* Header */}
      <header className="bg-white border-b border-gray-300 py-4 px-6 md:px-12" data-id="element-2279">
        <Link to="/" className="flex items-center gap-2" data-id="element-2280">
          <div className="w-8 h-8 bg-[#1f2933] rounded-sm" data-id="element-2281"></div>
          <span className="font-bold text-xl tracking-tight" data-id="element-2282">
            Citiplots.com
          </span>
        </Link>
      </header>

      <main className="max-w-md mx-auto px-4 py-12" data-id="element-2283">
        <div className="bg-white border border-gray-300 p-8 shadow-sm" data-id="element-2284">
          <h1 className="text-2xl font-bold mb-2" data-id="element-2285">Create Account</h1>
          <p className="text-sm text-gray-500 mb-6" data-id="element-2286">
            Join India's direct plot transaction platform
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2287">
            {/* User Type Selection */}
            <div data-id="element-2288">
              <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-2289">
                I am a
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-2290">
                {[{
                value: 'buyer',
                label: 'Buyer'
              }, {
                value: 'seller',
                label: 'Seller'
              }, {
                value: 'developer',
                label: 'Developer'
              }, {
                value: 'nri',
                label: 'NRI'
              }].map(type => <button key={type.value} type="button" onClick={() => setFormData({
                ...formData,
                userType: type.value
              })} className={`p-3 border text-sm font-medium ${formData.userType === type.value ? 'border-[#1f2933] bg-gray-50' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-2291">
                    {type.label}
                  </button>)}
              </div>
            </div>

            {/* Full Name */}
            <div data-id="element-2292">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2293">
                Full Name
              </label>
              <input type="text" required value={formData.fullName} onChange={e => setFormData({
              ...formData,
              fullName: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2294" />
            </div>

            {/* Email */}
            <div data-id="element-2295">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2296">
                Email Address
              </label>
              <input type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2297" />
            </div>

            {/* Phone */}
            <div data-id="element-2298">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2299">
                Phone Number
              </label>
              <input type="tel" required placeholder="+91" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2300" />
            </div>

            {/* Password */}
            <div data-id="element-2301">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2302">
                Password
              </label>
              <div className="relative" data-id="element-2303">
                <input type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2304" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2305">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-2306" /> : <Eye className="w-4 h-4" data-id="element-2307" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div data-id="element-2308">
              <label className="block text-sm font-bold text-gray-700 mb-1" data-id="element-2309">
                Confirm Password
              </label>
              <input type="password" required value={formData.confirmPassword} onChange={e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            })} className="w-full p-2 border border-gray-300 bg-gray-50 focus:outline-none focus:border-gray-500" data-id="element-2310" />
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start gap-2" data-id="element-2311">
              <input type="checkbox" id="terms" checked={formData.agreeToTerms} onChange={e => setFormData({
              ...formData,
              agreeToTerms: e.target.checked
            })} className="mt-1" data-id="element-2312" />
              <label htmlFor="terms" className="text-xs text-gray-600" data-id="element-2313">
                I agree to the{' '}
                <Link to="/terms" className="text-blue-600 underline" data-id="element-2314">
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-blue-600 underline" data-id="element-2315">
                  Privacy Policy
                </Link>
              </label>
            </div>

            {/* Submit Button */}
            <button type="submit" className="w-full py-3 bg-[#1f2933] text-white font-bold hover:bg-gray-800" data-id="element-2316">
              Create Account
            </button>
          </form>

          {/* Social Login */}
          <div className="mt-6" data-id="element-2317">
            <div className="relative" data-id="element-2318">
              <div className="absolute inset-0 flex items-center" data-id="element-2319">
                <div className="w-full border-t border-gray-300" data-id="element-2320"></div>
              </div>
              <div className="relative flex justify-center text-xs" data-id="element-2321">
                <span className="bg-white px-2 text-gray-500" data-id="element-2322">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2" data-id="element-2323">
              {['Google', 'LinkedIn', 'Facebook'].map(provider => <button key={provider} type="button" onClick={() => handleSocialLogin(provider)} className="py-2 border border-gray-300 text-xs font-medium hover:bg-gray-50" data-id="element-2324">
                  {provider}
                </button>)}
            </div>
          </div>

          {/* Sign In Link */}
          <p className="mt-6 text-center text-sm text-gray-600" data-id="element-2325">
            Already have an account?{' '}
            <Link to="/auth/signin" className="text-blue-600 font-bold hover:underline" data-id="element-2326">
              Sign In
            </Link>
          </p>
        </div>
      </main>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-2327" />
    </div>;
}